import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Switch } from "~/components/ui/switch";

import { SettingsSection, ToggleField } from "./form-fields";
import type { SectionProps } from "./types";

export function OnlineHoursSection({ form, onUpdate }: SectionProps) {
  const useCustom = form.watch("useCustomOnlineHours");
  const hours = form.watch("onlineOrderingHours");

  return (
    <SettingsSection
      description="Set separate hours for online ordering (defaults to business hours)"
      title="Online Ordering Hours"
    >
      <ToggleField
        description="Use different hours for online orders than your business hours"
        form={form}
        label="Custom Online Hours"
        name="useCustomOnlineHours"
        onUpdate={onUpdate}
      />
      {useCustom && (
        <div className="mt-4 space-y-2">
          {hours.map((day, index) => (
            <div
              className="flex items-center gap-3 rounded-lg border p-3"
              key={day.dayOfWeek}
            >
              <div className="w-24 text-sm font-medium">{day.dayOfWeek}</div>
              <Switch
                checked={day.isOpen}
                onCheckedChange={(checked) => {
                  form.setValue(`onlineOrderingHours.${index}.isOpen`, checked);
                  onUpdate();
                }}
              />
              <span className="text-muted-foreground text-xs">
                {day.isOpen ? "Open" : "Closed"}
              </span>
              {day.isOpen && (
                <>
                  <Input
                    className="w-28"
                    onBlur={() => onUpdate()}
                    onChange={(e) =>
                      form.setValue(
                        `onlineOrderingHours.${index}.openTime`,
                        e.target.value
                      )
                    }
                    type="time"
                    value={day.openTime}
                  />
                  <span className="text-muted-foreground text-sm">to</span>
                  <Input
                    className="w-28"
                    onBlur={() => onUpdate()}
                    onChange={(e) =>
                      form.setValue(
                        `onlineOrderingHours.${index}.closeTime`,
                        e.target.value
                      )
                    }
                    type="time"
                    value={day.closeTime}
                  />
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </SettingsSection>
  );
}

export function ScheduleOverridesSection({ form, onUpdate }: SectionProps) {
  const overrides = form.watch("scheduleOverrides");

  const addOverride = () => {
    const today = new Date();
    const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    form.setValue("scheduleOverrides", [
      ...overrides,
      {
        date: dateStr,
        type: "closed" as const,
        openTime: "09:00",
        closeTime: "21:00",
        reason: "",
      },
    ]);
    onUpdate();
  };

  const removeOverride = (index: number) => {
    form.setValue(
      "scheduleOverrides",
      overrides.filter((_, i) => i !== index)
    );
    onUpdate();
  };

  return (
    <SettingsSection
      description="Override hours for specific dates (holidays, special events)"
      title="Schedule Overrides"
    >
      {overrides.length > 0 && (
        <div className="space-y-3">
          {overrides.map((override, index) => (
            <div
              className="flex flex-wrap items-center gap-3 rounded-lg border p-3"
              key={override.date + index.toString()}
            >
              <Input
                className="w-40"
                onBlur={() => onUpdate()}
                onChange={(e) =>
                  form.setValue(
                    `scheduleOverrides.${index}.date`,
                    e.target.value
                  )
                }
                type="date"
                value={override.date}
              />
              <select
                className="bg-background rounded-md border px-3 py-2 text-sm"
                onChange={(e) => {
                  const nextType = e.target.value;
                  if (nextType !== "closed" && nextType !== "modified") {
                    return;
                  }
                  form.setValue(`scheduleOverrides.${index}.type`, nextType);
                  onUpdate();
                }}
                value={override.type}
              >
                <option value="closed">Closed</option>
                <option value="modified">Modified Hours</option>
              </select>
              {override.type === "modified" && (
                <>
                  <Input
                    className="w-28"
                    onBlur={() => onUpdate()}
                    onChange={(e) =>
                      form.setValue(
                        `scheduleOverrides.${index}.openTime`,
                        e.target.value
                      )
                    }
                    type="time"
                    value={override.openTime}
                  />
                  <span className="text-muted-foreground text-sm">to</span>
                  <Input
                    className="w-28"
                    onBlur={() => onUpdate()}
                    onChange={(e) =>
                      form.setValue(
                        `scheduleOverrides.${index}.closeTime`,
                        e.target.value
                      )
                    }
                    type="time"
                    value={override.closeTime}
                  />
                </>
              )}
              <Input
                className="w-40"
                onBlur={() => onUpdate()}
                onChange={(e) =>
                  form.setValue(
                    `scheduleOverrides.${index}.reason`,
                    e.target.value
                  )
                }
                placeholder="Reason (optional)"
                value={override.reason}
              />
              <Button
                onClick={() => removeOverride(index)}
                size="sm"
                type="button"
                variant="ghost"
              >
                Remove
              </Button>
            </div>
          ))}
        </div>
      )}
      <Button
        className="mt-3"
        onClick={addOverride}
        size="sm"
        type="button"
        variant="outline"
      >
        Add Override
      </Button>
    </SettingsSection>
  );
}
